import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Segmented,
  Select,
  Space,
  Typography,
  theme,
} from 'antd';
import Tag from '@/components/Tag';
import { CheckCard } from '@ant-design/pro-components';
import dayjs from 'dayjs';
import MediaUploader from '@/components/MediaUploader';
import PerformanceOwnershipSection from '@/components/PerformanceOwnershipSection';
import {
  assignPerformanceOwnershipAmountsForSubmit,
  buildDefaultPerformanceOwnershipItem,
  getFirstValidationErrorMessage,
} from '@/components/PerformanceOwnershipSection/utils';
import { IStudioInfo } from '@/types/studio';
import { MONEY_REGEX, POSITIVE_INTEGER_REGEX } from '@/utils/validate';
import { ActivationMode, MemberInfo, UserType } from '@/types/member';
import { ClassInfo, ClassStatus } from '@/types/class';
import { getClassList } from '@/services/class';
import { PerformanceOwnershipItem, StaffInfo } from '@/types/staff';
import { getStaffList } from '@/services/staff';
import { MemberCardInfo } from '@/types/member-card';
import { formatBookLimit, formatTimeRules, formatUseStore } from '@/utils/memberCardRuleUtils';
import { formatPriceCentToYuan, formatPriceYuanToCent } from '@/utils';
import { createUserMemberCard, CreateUserMemberCardParams, getMemberInfo } from '@/services/member';
import { useMerchantConfig } from '@/hooks/useMerchantConfig';
import { EnableStatus, PaymentType } from '@/types/common';
import { createMemberCardOrder } from '@/services/order';
import { getCouponIssueRecordList } from '@/services/coupon';
import { CouponTemplateType } from '@/constants/coupon';
import { CouponItem, createMemberCardCoupons } from '@/utils/coupon/createMemberCardCoupons';
import { getFinalPrice } from '@/utils/coupon/common';
import { CollectionMethod } from '@/constants/merchant';
import { usePermissionControl } from '@/hooks/usePermissionControl';
import { ACTION_CONTROL } from '@/constants/action-permission';
import { getStudioList } from '@/services/studio';
import { OpenCardCompleteResult } from '@/types/member';

export type OpenCardSuccessResult = OpenCardCompleteResult;

function getActivateTypeLabel(activateType: ActivationMode): string {
  if (activateType === ActivationMode.IMMEDIATELY) {
    return '办卡后立即激活';
  }
  return '首次签到激活';
}

function normalizeActivateType(value?: number | null): ActivationMode | null {
  if (value === ActivationMode.IMMEDIATELY || value === ActivationMode.AUTO_ACTIVATE) {
    return value;
  }
  return null;
}

interface IProps {
  open: boolean;
  data: MemberInfo; // 会员详情
  studioList: IStudioInfo[]; // 门店列表
  cardList: MemberCardInfo[]; // 会员卡列表
  onClose: () => void;
  embedded?: boolean;
  memberInfoFormat?: 'id' | 'phone';
  offlineConfirmText?: string;
  onSuccess?: (result: OpenCardSuccessResult) => void;
  onFormDirtyChange?: (dirty: boolean) => void;
  embeddedCancelText?: string;
  embeddedFooterFixed?: boolean;
  embeddedClassNames?: {
    layout?: string;
    scroll?: string;
    footer?: string;
  };
}

type OpenCardUserInfo = {
  store_id?: number;
  store_name?: string;
  admin_id?: number;
  admin_name?: string;
};

const sectionStyle: React.CSSProperties = {
  marginBottom: 24,
  padding: 20,
  borderRadius: 20,
  background: 'rgba(248, 250, 255, 0.9)',
  border: '1px solid rgba(23, 32, 51, 0.06)',
};

const ruleBoxStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 8,
  background: 'linear-gradient(180deg, rgba(247, 249, 252, 0.9), rgba(255, 255, 255, 0.96))',
  border: '1px solid rgba(0, 0, 0, 0.06)',
};

const ruleRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  marginTop: 8,
  fontSize: 14,
  lineHeight: '22px',
};

const ruleLabelStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 72,
  color: 'rgba(0, 0, 0, 0.45)',
};

export default function DrawerOpenCard({
  open,
  data,
  studioList,
  cardList,
  onClose,
  embedded = false,
  memberInfoFormat = 'id',
  offlineConfirmText,
  onSuccess,
  onFormDirtyChange,
  embeddedCancelText,
  embeddedFooterFixed = false,
  embeddedClassNames,
}: IProps) {
  const { token } = theme.useToken();
  const merchantConfig = useMerchantConfig();
  const { hasPermission } = usePermissionControl();

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();
  const [storeList, setStoreList] = useState<IStudioInfo[]>(studioList); // 门店列表
  const [staffList, setStaffList] = useState<StaffInfo[]>([]); // 员工列表
  const [couponList, setCouponList] = useState<CouponItem[]>([]); // 优惠券列表
  const [openCardUserInfo, setOpenCardUserInfo] = useState<OpenCardUserInfo>({});
  const [classList, setClassList] = useState<ClassInfo[]>([]);
  const [classListLoading, setClassListLoading] = useState(false);

  const isChildUser = data.user_type === UserType.CHILD;
  const memberCardId = Form.useWatch('member_card_id', form); // 会员卡ID
  const activateType = Form.useWatch('activate_type', form); // 激活方式
  const payMethod = Form.useWatch('pay_method', form); // 收款方式
  const issuingAdminId = Form.useWatch('issuing_admin_id', form); // 开卡负责人
  const issuingStoreId = Form.useWatch('issuing_store_id', form); // 开卡门店
  const actualAmount = Form.useWatch('actual_amount', form); // 实收金额
  const actualAmountInCent = actualAmount ? formatPriceYuanToCent(Number(actualAmount)) : undefined;
  const fixedStoreId = openCardUserInfo.store_id;
  const fixedStoreName = openCardUserInfo.store_name || undefined;
  const fixedAdminId = openCardUserInfo.admin_id;
  const fixedAdminName = openCardUserInfo.admin_name || undefined;

  const memberCardDetail = cardList.find((item) => item.id === memberCardId); // 当前选择的会员卡信息
  const isOfflinePayment = payMethod === 'offline';
  const currentIssuingAdmin = staffList.find((item) => item.id === issuingAdminId);
  const storeOptions = useMemo(() => {
    const options = storeList.map((item) => ({ label: item.name, value: item.id }));
    if (fixedStoreId && !options.some((item) => item.value === fixedStoreId)) {
      options.unshift({
        label: fixedStoreName || `门店(${fixedStoreId})`,
        value: fixedStoreId,
      });
    }
    return options;
  }, [fixedStoreId, fixedStoreName, storeList]);
  const staffOptions = useMemo(() => {
    const options = staffList.map((item) => ({ label: item.username, value: item.id }));
    if (fixedAdminId && !options.some((item) => item.value === fixedAdminId)) {
      options.unshift({
        label: fixedAdminName || `负责人(${fixedAdminId})`,
        value: fixedAdminId,
      });
    }
    return options;
  }, [fixedAdminId, fixedAdminName, staffList]);
  const groupedCardOptions = useMemo(() => {
    const groupMap = new Map<
      number,
      { label: string; options: { label: string; value: number }[] }
    >();
    const groupOrder: number[] = [];

    for (const item of cardList) {
      const typeId = item.member_card_type_id ?? -1;
      if (!groupMap.has(typeId)) {
        groupMap.set(typeId, {
          label: item.member_card_type_name || '未分类',
          options: [],
        });
        groupOrder.push(typeId);
      }
      groupMap.get(typeId)!.options.push({
        label: item.name,
        value: item.id,
      });
    }

    return groupOrder.map((typeId) => groupMap.get(typeId)!);
  }, [cardList]);
  const defaultPerformanceOwner = useMemo(
    () => ({
      ownerId: issuingAdminId,
      ownerName:
        issuingAdminId === fixedAdminId
          ? fixedAdminName
          : currentIssuingAdmin?.username || undefined,
    }),
    [currentIssuingAdmin?.username, fixedAdminId, fixedAdminName, issuingAdminId],
  );
  const classOptions = useMemo(
    () =>
      classList.map((item) => ({
        label: `${item.name} | ${item.teacher_name} | ${item.in_count}/${item.max_students}人`,
        value: item.id,
        disabled: item.in_count >= item.max_students,
      })),
    [classList],
  );

  // 请求门店列表
  async function requestStudioOptions() {
    const res = await getStudioList();
    setStoreList(res?.list || []);
  }

  // 请求用户详情（用于开卡门店/负责人回填）
  async function requestOpenCardUserInfo() {
    if (!data?.user_id) {
      setOpenCardUserInfo({});
      return {};
    }
    try {
      const res = await getMemberInfo(Number(data.user_id));
      const nextInfo: OpenCardUserInfo = {
        store_id: res?.store_id || undefined,
        store_name: res?.store_name || undefined,
        admin_id: res?.admin_id || undefined,
        admin_name: res?.admin_name || undefined,
      };
      setOpenCardUserInfo(nextInfo);
      return nextInfo;
    } catch (error) {
      setOpenCardUserInfo({});
      return {};
    }
  }

  // 请求员工列表
  async function requestStaffList(storeId?: number) {
    if (!storeId) {
      setStaffList([]);
      return;
    }
    const res = await getStaffList({ store_id: storeId, page_size: 100 });
    setStaffList(res?.list || []);
  }

  // 请求班级列表（少儿开卡后加入班级）
  async function requestClassList(storeId?: number) {
    if (!storeId) {
      setClassList([]);
      return;
    }
    try {
      setClassListLoading(true);
      const res = await getClassList({
        store_id: storeId,
        status: ClassStatus.Ongoing,
        page: 1,
        page_size: 100,
      });
      setClassList(res?.list || []);
    } catch (error) {
      console.error('获取班级列表失败', error);
      setClassList([]);
    } finally {
      setClassListLoading(false);
    }
  }

  // 切换门店
  function onStudioChange(value?: number) {
    // 清空开卡负责人的选中状态
    if (!fixedAdminId) {
      form.setFieldsValue({
        issuing_admin_id: undefined,
      });
    }
    // 清空员工列表
    setStaffList([]);
    // 请求当前门店下的员工列表
    requestStaffList(value);
  }

  // 请求优惠券列表
  async function requestCouponList() {
    if (!data?.user_id) return;
    try {
      // 请求用户的会员卡代金券列表
      const res = await getCouponIssueRecordList({
        page: 1,
        page_size: 100,
        template_type: CouponTemplateType.MemberCardCashCoupon, // 会员卡代金券
        user_id: data.user_id,
        is_active: EnableStatus.Enabled,
      });
      const list = res?.list || [];

      // 如果有选中的会员卡，直接计算
      const newCouponList = createMemberCardCoupons(
        // 默认打开抽屉的时候，还没有选择会员卡，所以会员卡数据为空
        undefined,
        list,
        dayjs().format('YYYY-MM-DD HH:mm:ss'),
      );
      setCouponList(newCouponList);
    } catch (error) {
      console.error('获取优惠券列表失败', error);
    }
  }

  // 初始化表单
  async function initForm() {
    const userInfo = await requestOpenCardUserInfo();
    const currentStoreId = userInfo.store_id || undefined;
    const currentAdminId = userInfo.admin_id || undefined;
    const currentAdminName = userInfo.admin_name || undefined;

    if (!currentStoreId) {
      await requestStudioOptions();
    }

    // 请求当前门店下的员工列表
    if (currentStoreId) {
      await requestStaffList(currentStoreId);
    }

    // 根据 data.admin_name 是否为空来设置表单值
    // 如果有负责人，设置开卡门店和开卡负责人，并设置为不可选中状态
    form.setFieldsValue({
      issuing_store_id: currentStoreId,
      issuing_admin_id: currentAdminId,
      allocation: [
        buildDefaultPerformanceOwnershipItem({
          ownerId: currentAdminId,
          ownerName: currentAdminName,
        }),
      ],
    });
  }

  useEffect(() => {
    setStoreList(studioList);
  }, [studioList]);

  useEffect(() => {
    if (open) {
      initForm();
      requestCouponList();
    }
  }, [open, data?.user_id]);

  useEffect(() => {
    if (!open || !isChildUser || !issuingStoreId) {
      setClassList([]);
      return;
    }
    form.setFieldValue('class_id', undefined);
    requestClassList(issuingStoreId);
  }, [open, issuingStoreId, isChildUser]);

  // 切换会员卡
  function onMemberCardChange(value: number) {
    const memberCardDetail = cardList.find((item) => item.id === value);
    const normalizedActivateType = normalizeActivateType(memberCardDetail?.activate_type);
    // 根据当前选中的会员卡，设置默认值
    const formData: Record<string, any> = {
      valid_days: memberCardDetail?.validity_period || undefined,
      activate_type: normalizedActivateType,
      auto_activate_days:
        normalizedActivateType === ActivationMode.AUTO_ACTIVATE
          ? memberCardDetail?.auto_activate_days || undefined
          : null,
      // 设置实收金额为会员卡的折扣价
      actual_amount: memberCardDetail?.discount_price
        ? formatPriceCentToYuan(memberCardDetail.discount_price)
        : undefined,
      // 切换会员卡时清空代金券选择
      coupon_issue_id: undefined,
    };

    // 如果是次卡，设置默认次数
    if (memberCardDetail?.payment_type === PaymentType.TIMES) {
      formData.total_card_num = memberCardDetail?.num_of_times;
    } else {
      // 如果不是次卡，清空次数字段
      formData.total_card_num = undefined;
    }

    form.setFieldsValue(formData);

    // 切换会员卡的时候，更新优惠券列表的状态
    if (memberCardDetail) {
      const newCouponList = createMemberCardCoupons(
        memberCardDetail as MemberCardInfo,
        couponList,
        dayjs().format('YYYY-MM-DD HH:mm:ss'),
      );
      setCouponList(newCouponList);
    }
  }

  // 切换优惠券
  function onCouponChange(couponId: number | undefined) {
    const memberCardDetail = cardList.find((item) => item.id === memberCardId);
    if (!memberCardDetail?.discount_price) return;

    // 找到选中的优惠券
    const selectedCoupon = couponList.find((item) => item.id === couponId);

    // 计算最终价格
    const finalPrice = getFinalPrice(memberCardDetail.discount_price, selectedCoupon);

    // 更新实收金额
    form.setFieldsValue({
      actual_amount: formatPriceCentToYuan(finalPrice),
    });
  }

  async function onFinish() {
    try {
      setConfirmLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const allocationList = (form.getFieldValue('allocation') || []) as Array<
        PerformanceOwnershipItem & { is_default?: boolean }
      >;
      const currentActualAmountInCent = values.actual_amount
        ? formatPriceYuanToCent(Number(values.actual_amount))
        : undefined;
      const allocationWithAmount = assignPerformanceOwnershipAmountsForSubmit(
        allocationList,
        currentActualAmountInCent,
      );

      const payload: Partial<CreateUserMemberCardParams> = {
        member_card_id: values.member_card_id,
        activate_type: values.activate_type,
        issuing_store_id: values.issuing_store_id,
        issuing_admin_id: values.issuing_admin_id,
        issuing_remarks: values.issuing_remarks,
        pay_image: values.pay_image,
        user_id: Number(data.user_id),
        actual_amount: currentActualAmountInCent, // 价格转换为分
        allocation: allocationWithAmount.map((item) => ({
          allocate_id: item.allocate_id,
          allocate_type: item.allocate_type,
          ratio: Number(item.ratio || 0),
          amount: item.amount,
          remark: item.is_default && !item.remark?.trim() ? '开卡负责人' : item.remark,
        })),
      };

      // 如果选择了优惠券，则添加优惠券ID
      if (values.coupon_issue_id) {
        payload.coupon_issue_id = values.coupon_issue_id;
      }

      // 自定义开卡次数
      if (values.total_card_num) {
        payload.total_card_num = +values.total_card_num;
      }

      // 有效期(天数)
      if (values.valid_days) {
        payload.valid_days = +values.valid_days;
      }

      // 自动激活天数
      if (values.auto_activate_days) {
        payload.auto_activate_days = +values.auto_activate_days;
      }

      // 如果激活方式为立即激活，则删除自动激活天数
      if (values.activate_type === ActivationMode.IMMEDIATELY) {
        delete payload.auto_activate_days;
      }

      if (isChildUser && values.class_id) {
        payload.class_id = values.class_id;
      }

      const selectedStore = storeList.find((item) => item.id === values.issuing_store_id);
      const selectedAdmin = staffList.find((item) => item.id === values.issuing_admin_id);
      const successResult: OpenCardSuccessResult = {
        payMethod: isOfflinePayment ? 'offline' : 'online',
        userId: Number(data.user_id),
        userName: data.name,
        phoneNumber: data.phone_number,
        memberCardName: memberCardDetail?.name || '',
        memberCardTypeName: memberCardDetail?.member_card_type_name || '',
        totalCardNum: values.total_card_num ? Number(values.total_card_num) : undefined,
        validDays: Number(values.valid_days),
        actualAmount: Number(values.actual_amount),
        activateTypeLabel: getActivateTypeLabel(values.activate_type),
        issuingStoreName: selectedStore?.name || openCardUserInfo.store_name || '',
        issuingAdminName: selectedAdmin?.username || openCardUserInfo.admin_name || '',
      };

      if (isOfflinePayment) {
        await createUserMemberCard(payload);
        message.success('开卡成功');
        if (onSuccess) {
          onSuccess(successResult);
          return;
        }
      } else {
        const res = await createMemberCardOrder({
          ...payload,
          original_amount: memberCardDetail?.price,
        });
        if (onSuccess) {
          onSuccess({ ...successResult, orderNo: res.order_no });
          return;
        }
        window.open(`/finance-management/order-detail/${res.order_no}`);
      }

      onClose();
    } catch (error: any) {
      // 表单错误
      if (error?.errorFields?.length > 0) {
        const firstErrorMessage = getFirstValidationErrorMessage(error);
        if (firstErrorMessage) {
          message.error(firstErrorMessage);
        }

        const firstErrorField = error.errorFields?.[0]?.name;
        if (firstErrorField) {
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'center',
          });
        }
        return;
      }

      // 创建订单请求
      if (!isOfflinePayment) {
        if (error?.name === 'AxiosError' && error?.response?.status) {
          // http 请求错误
          message.error(error?.response?.data?.message || '创建订单失败');
        } else {
          // http 请求成功，但业务失败
          if (error?.code > 0 && error?.data?.order_no) {
            // 存在待支付的订单
            Modal.warning({
              title: '提示',
              content: error.message,
              okText: '去查看',
              onOk: () => {
                window.open(`/finance-management/order-detail/${error.data?.order_no}`);
              },
            });
          } else if (error.code === 20827) {
            // 门店未设置收款账户
            Modal.warning({
              title: '提示',
              content: error.message,
              okText: '去设置',
              onOk: () => {
                window.open(`/finance-management/payment-account`);
              },
            });
          } else {
            message.error(error?.message || '创建订单失败');
          }
        }
      }
    } finally {
      setConfirmLoading(false);
    }
  }

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  if (!data?.user_id) {
    return null;
  }

  const memberInfoText =
    memberInfoFormat === 'phone'
      ? `${data.name}（${data.phone_number || '-'}）`
      : `${data.name} (ID: ${data.user_id})`;

  const footer = (
    <Flex justify="end" gap="small">
      {embedded ? (
        embeddedCancelText && <Button onClick={onClose}>{embeddedCancelText}</Button>
      ) : (
        <Button onClick={onClose}>取消</Button>
      )}
      <Button onClick={onFinish} type="primary" loading={confirmLoading}>
        {isOfflinePayment ? offlineConfirmText || '确认' : '创建订单'}
      </Button>
    </Flex>
  );

  const formContent = (
    <Form
      layout="horizontal"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 16 }}
      form={form}
      onValuesChange={() => {
        onFormDirtyChange?.(true);
      }}
    >
      <div style={sectionStyle}>
        <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
          开卡信息
        </Typography.Title>

        <Form.Item label="会员信息">
          <span>{memberInfoText}</span>
        </Form.Item>

        <Form.Item
          name="member_card_id"
          label="会员卡"
          rules={[{ required: true, message: '请选择会员卡' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="请选择会员卡"
            options={groupedCardOptions}
            onChange={(value) => onMemberCardChange(value)}
          />
        </Form.Item>

        {memberCardDetail && (
          <Form.Item label=" " colon={false}>
            <div style={ruleBoxStyle}>
              <Typography.Text strong style={{ fontSize: 14 }}>
                使用规则
              </Typography.Text>
              <div style={ruleRowStyle}>
                <span style={ruleLabelStyle}>适用门店</span>
                <span>{formatUseStore(memberCardDetail)}</span>
              </div>
              <div style={ruleRowStyle}>
                <span style={ruleLabelStyle}>可用时段</span>
                <span style={{ whiteSpace: 'pre-line', flex: 1 }}>
                  {formatTimeRules(memberCardDetail)}
                </span>
              </div>
              <div style={{ ...ruleRowStyle, marginBottom: 0 }}>
                <span style={ruleLabelStyle}>约课限制</span>
                <span>{formatBookLimit(memberCardDetail, { showBooked: false })}</span>
              </div>
            </div>
          </Form.Item>
        )}

        {memberCardDetail?.payment_type === PaymentType.TIMES && (
          <Form.Item
            name="total_card_num"
            label="次数"
            rules={[
              { required: true, message: '请输入次数' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const inputValue = String(value).trim();
                  return /^(?:[1-9]\d*|(?:0|[1-9]\d*)?\.5)$/.test(inputValue)
                    ? Promise.resolve()
                    : Promise.reject(new Error('仅允许输入整数或以0.5为最小单位'));
                },
              },
            ]}
          >
            <Input placeholder="请输入次数" addonAfter="次" />
          </Form.Item>
        )}

        <Form.Item
          name="valid_days"
          label="有效期(天数)"
          rules={[
            { required: true, message: '请输入有效期（天数）' },
            { pattern: POSITIVE_INTEGER_REGEX, message: '请输入大于0的整数' },
          ]}
        >
          <Input placeholder="请输入天数" addonAfter="天" />
        </Form.Item>

        <Form.Item label="会员卡价格">
          {memberCardDetail ? (
            <Space size="small">
              <Typography.Text strong style={{ fontSize: 16, color: token.colorError }}>
                ¥{formatPriceCentToYuan(memberCardDetail.discount_price || 0)}
              </Typography.Text>
              {memberCardDetail.price !== memberCardDetail.discount_price && (
                <Typography.Text delete type="secondary">
                  ¥{formatPriceCentToYuan(memberCardDetail.price || 0)}
                </Typography.Text>
              )}
            </Space>
          ) : (
            <Typography.Text disabled>选择会员卡后显示</Typography.Text>
          )}
        </Form.Item>

        <Form.Item name="coupon_issue_id" label="代金券">
          <Select
            placeholder={couponList.length > 0 ? '请选择代金券' : '暂无代金券'}
            allowClear
            optionLabelProp="label"
            onChange={onCouponChange}
            options={couponList.map((item) => {
              const memberCardTypeName = item.coupon_template_info?.member_card_type_id
                ? item.coupon_template_info?.member_card_type_name
                : '所有卡种';
              return {
                label: `${item.name}`,
                value: item.id,
                disabled: item.disabled,
                subname: `${item.subname} (${memberCardTypeName}可用)`,
              };
            })}
            optionRender={(option) => (
              <div style={{ position: 'relative' }}>
                {option.data.disabled && (
                  <Tag
                    color="default"
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      margin: 0,
                    }}
                  >
                    不可用
                  </Tag>
                )}
                <div>{option.data.label}</div>
                <Typography.Text disabled style={{ fontSize: 12, marginTop: 2 }}>
                  {option.data.subname}
                </Typography.Text>
              </div>
            )}
          />
        </Form.Item>

        <Form.Item
          name="actual_amount"
          label="实收金额"
          rules={[
            { required: true, message: '请输入实收金额' },
            { pattern: MONEY_REGEX, message: '实收金额格式不正确' },
          ]}
        >
          <Input placeholder="请输入实收金额（元）" addonAfter="元" />
        </Form.Item>

        <Form.Item
          name="pay_method"
          label="收款方式"
          rules={[{ required: true, message: '请选择收款方式' }]}
        >
          <CheckCard.Group style={{ display: 'flex' }}>
            <div
              style={{ flex: 1, marginRight: 12 }}
              onClickCapture={(e) => {
                if (!hasPermission(ACTION_CONTROL.CreateOrder)) {
                  e.stopPropagation();
                  message.warning('无操作权限，请联系商家管理员');
                }
              }}
            >
              <CheckCard
                size="small"
                title={
                  <Space>
                    <span>在线支付</span>
                    <Tag color="blue">推荐</Tag>
                  </Space>
                }
                description="可签署电子合同，支持微信和支付宝付款"
                value="online"
                style={{ width: '100%', marginBottom: 0 }}
              />
            </div>
            <div
              style={{ flex: 1 }}
              onClickCapture={(e) => {
                if (!hasPermission(ACTION_CONTROL.QuickOpenCard)) {
                  e.stopPropagation();
                  message.warning('无操作权限，请联系商家管理员');
                }
              }}
            >
              <CheckCard
                size="small"
                title="线下收款"
                description="系统仅记账，无法关联电子合同"
                value="offline"
                style={{ width: '100%', marginBottom: 0 }}
                disabled={merchantConfig?.collect_pay_type === CollectionMethod.OnlineOnly}
              />
            </div>
          </CheckCard.Group>
        </Form.Item>

        <Form.Item
          name="activate_type"
          label="激活方式"
          rules={[{ required: true, message: '请选择激活方式' }]}
        >
          <Segmented
            options={[
              { label: '办卡后立即激活', value: ActivationMode.IMMEDIATELY },
              { label: '首次签到激活', value: ActivationMode.AUTO_ACTIVATE },
            ]}
          />
        </Form.Item>
        {activateType === ActivationMode.AUTO_ACTIVATE && (
          <Form.Item label=" " colon={false}>
            <Space size="small">
              <Form.Item
                name="auto_activate_days"
                noStyle
                rules={[
                  { required: true, message: '请输入天数' },
                  { pattern: POSITIVE_INTEGER_REGEX, message: '请输入大于0的整数' },
                ]}
              >
                <Input style={{ width: 80 }} />
              </Form.Item>
              <span>天未上课，到期自动激活</span>
            </Space>
          </Form.Item>
        )}

        <Form.Item
          name="issuing_store_id"
          label="开卡门店"
          rules={[{ required: true, message: '请选择开卡门店' }]}
        >
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="请选择开卡门店"
            options={storeOptions}
            onChange={(value) => onStudioChange(value)}
            disabled={!!fixedStoreId}
          />
        </Form.Item>

        <Form.Item
          name="issuing_admin_id"
          label="开卡负责人"
          rules={[{ required: true, message: '请选择开卡负责人' }]}
        >
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="请选择开卡负责人"
            options={staffOptions}
            disabled={!!fixedAdminId}
          />
        </Form.Item>

        <Form.Item name="issuing_remarks" label="开卡备注">
          <Input.TextArea placeholder="请输入开卡备注" rows={4} />
        </Form.Item>

        {isOfflinePayment && (
          <Form.Item name="pay_image" label="付款凭证">
            <MediaUploader
              value={data?.avatar}
              onChange={(value) => {
                form.setFieldsValue({
                  pay_image: value,
                });
              }}
            />
          </Form.Item>
        )}
      </div>

      <PerformanceOwnershipSection
        title="业绩归属"
        form={form}
        storeId={issuingStoreId}
        totalAmount={actualAmountInCent}
        defaultOwner={defaultPerformanceOwner}
      />

      {isChildUser && (
        <div
          style={{
            ...sectionStyle,
            marginTop: 24,
            padding: '18px 22px',
          }}
        >
          <Form.Item
            name="class_id"
            colon={false}
            labelCol={{ span: 6, style: { textAlign: 'left' } }}
            wrapperCol={{ span: 16 }}
            label={
              <Typography.Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                开卡后加入班级
              </Typography.Title>
            }
            style={{ marginBottom: 0 }}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={issuingStoreId ? '请选择班级（选填）' : '请先选择开卡门店'}
              loading={classListLoading}
              options={classOptions}
              disabled={!issuingStoreId}
            />
          </Form.Item>
        </div>
      )}
    </Form>
  );

  if (embedded) {
    if (!open) {
      return null;
    }
    if (embeddedFooterFixed) {
      return (
        <div className={embeddedClassNames?.layout}>
          <div className={embeddedClassNames?.scroll}>{formContent}</div>
          <div className={embeddedClassNames?.footer}>{footer}</div>
        </div>
      );
    }
    return (
      <div>
        {formContent}
        <div style={{ marginTop: 24 }}>{footer}</div>
      </div>
    );
  }

  return (
    <Drawer
      title="开卡"
      width={760}
      onClose={onClose}
      open={open}
      maskClosable={false}
      styles={{
        body: {
          paddingBottom: 80,
        },
      }}
      footer={footer}
      afterOpenChange={(visible) => {
        if (!visible) {
          form.resetFields();
        }
      }}
    >
      {formContent}
    </Drawer>
  );
}
